/**
 * SterileProcessingCommandCenterPage — /sterile-processing
 *
 * Dedicated "Sterile Processing command center" landing page mirroring the
 * Hemodialysis Command Center pattern (/dialysis). It bundles the MicroCare
 * (ProEZ, ProLube, ProSpray, ProChek, etc.) SKUs from the marketplace with
 * the AAMI ST91 / AAMI ST108 / AAMI TIR34 compliance resources into a single
 * B2B landing page hospitals, ASCs, dental/vet buyers, and RFP responders
 * can share.
 *
 * Product data comes from the shared @/data/sterileProcessingProducts module
 * with live case/unit pricing merged in from /api/microcare/catalog at load.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO, { technicalArticleSchema } from "@/components/SEO";
import SiteHeader from "@/components/SiteHeader";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import {
  FlaskConical,
  Beaker,
  Sparkles,
  ShieldCheck,
  Truck,
  Building2,
  CheckCircle2,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Award,
  FileText,
  Package,
  Droplets,
  ShoppingCart,
} from "lucide-react";
import {
  buildSterileProcessingProducts,
  fetchMicrocarePricing,
} from "@/data/sterileProcessingProducts";
import ProductDetailModal from "@/components/ProductDetailModal";

// ────────────────────────────────────────────────────────────────────────────
// Data
// ────────────────────────────────────────────────────────────────────────────

const PILLARS = [
  {
    icon: Beaker,
    title: "Enzymatic Detergents",
    kicker: "ProEZ · ProClense · ProWash",
    text: "Single-, dual-, and quadruple-enzyme instrument detergents for pre-cleaning, ultrasonic tanks, cart wash bays, and automatic washer-disinfectors.",
  },
  {
    icon: Droplets,
    title: "Point-of-Use Treatment",
    kicker: "ProEZ Foam · Gel · QEZ Kits",
    text: "Foaming sprays, cling gels, and single-use bedside kits keep soils moist from OR to SPD — cutting decontamination time and bioburden.",
  },
  {
    icon: FlaskConical,
    title: "Sterilization Indicators",
    kicker: "ProChek · ProView",
    text: "Class 4 chemical indicators, Class 5 steam integrators, and self-sealing sterilization pouches for cycle-by-cycle exposure verification.",
  },
  {
    icon: Sparkles,
    title: "Surface Disinfection",
    kicker: "ProSpray · V-Cide",
    text: "Hospital-grade RTU disinfectants, concentrates, wipes, and high-level chemical sterilants — TB/HBV/HCV/HIV kill claims on high-touch clinical surfaces.",
  },
  {
    icon: Package,
    title: "Instrument Lubrication & Rinse",
    kicker: "ProLube · ProRinse",
    text: "Autoclave-safe lubricants and washer rinse agents. Extend instrument life, eliminate spotting and streaking, accelerate drying cycles.",
  },
  {
    icon: ShieldCheck,
    title: "Waste & Safety",
    kicker: "Pro Tector · Biohazard bags",
    text: "OSHA-compliant sharps disposal, single-hand needle recapping devices, and heavy-duty red biohazard bags for regulated medical waste.",
  },
];

const STANDARDS_STACK = [
  { code: "AAMI ST79", note: "Comprehensive guide to steam sterilization + sterility assurance" },
  { code: "AAMI ST91", note: "Flexible & semi-rigid endoscope processing in health care" },
  { code: "AAMI ST108", note: "Water for processing medical devices (Category 1 → 3)" },
  { code: "AAMI TIR34", note: "Water for reprocessing medical devices — technical report" },
  { code: "AAMI ST58", note: "Chemical sterilization & high-level disinfection processing" },
  { code: "Joint Commission IC.02.02.01", note: "Reduces the risk of infections from equipment/devices" },
  { code: "OSHA 29 CFR 1910.1030", note: "Bloodborne pathogens — sharps + regulated waste handling" },
];

const COVERAGE = [
  { state: "Texas", locales: "Houston · TMC · Dallas · Fort Worth · San Antonio · Austin" },
  { state: "Louisiana", locales: "New Orleans · Baton Rouge · Shreveport · Lafayette" },
  { state: "Alabama", locales: "Birmingham · Mobile · Montgomery · Huntsville" },
  { state: "Mississippi", locales: "Jackson · Gulfport · Hattiesburg · Tupelo" },
];

const COMPARE = [
  {
    them: "Steris / STERIS Life Sciences",
    us: "Non-GPO, no exclusivity or minimum monthly commitments. Substitute equivalent SKUs across ProEZ, ProLube, and ProSpray lines without a supplier switch.",
  },
  {
    them: "Getinge · Ecolab distribution",
    us: "Locally dispatched from Houston. Same-day rush delivery when a washer-disinfector runs out of detergent mid-cycle — no 6-week national lead times.",
  },
  {
    them: "Regional SPD distributors",
    us: "Full stack: point-of-use treatment + enzymatic cleaners + sterilization indicators + lubricants + disinfectants + waste bags — one PO, one phone number.",
  },
];

const FAQS = [
  {
    q: "Do you supply hospitals, ASCs, dental offices, and veterinary hospitals?",
    a: "Yes. Every SKU in our Sterile Processing line is formulated for the same enzymatic, thermal-disinfection, and sterilization workflows used in hospital SPDs, ambulatory surgery centers, dental practices, and veterinary hospitals. We ship the same case sizes to all four channels.",
  },
  {
    q: "How do your enzymatic detergents map to AAMI ST79 and ST91 workflows?",
    a: "ProEZ 1 (single-enzyme) and ProEZ 2 (dual-enzyme) are formulated for the manual pre-cleaning and immersion soak steps described in AAMI ST79. ProEZ AW Quad and ProEZ SCD are automatic washer-disinfector detergents that meet ST79's mechanical-washer parameters. ProEZ Foam and ProEZ Gel are point-of-use pre-treatments that keep soils moist during transport, satisfying AAMI ST91's endoscope reprocessing requirements.",
  },
  {
    q: "What sterilization indicator classes do you supply?",
    a: "ProChek ID is a Class 4 multi-parameter chemical indicator for steam and chemical-vapor cycles. ProChek S is a Class 5 integrator that verifies time + temperature + saturated-steam conditions inside every load. ProView plus self-sealing pouches include dual Class 4 indicators built into the pouch material.",
  },
  {
    q: "Do the ProSpray disinfectants have EPA registration and clinical kill claims?",
    a: "Yes. ProSpray C-60 (concentrate), ProSpray RTU, and ProSpray Wipes are EPA-registered hospital-grade disinfectants with broad-spectrum kill claims that include TB, HBV, HCV, and HIV. V-Cide is a high-level chemical sterilant/disinfectant for heat-sensitive semi-critical devices such as endoscopes.",
  },
  {
    q: "How fast can you dispatch a rush order?",
    a: "Gulf-region dispatch is one business day for scheduled restocks and same-day for emergency deliveries out of our Houston operations center. We keep every SKU in this catalog staged for rapid deployment.",
  },
];

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

const money = (n) =>
  n == null || Number.isNaN(Number(n))
    ? null
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n));

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────

export default function SterileProcessingCommandCenterPage() {
  const canonical = "/sterile-processing";
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const { addItem, setShowCart } = useQuoteCart();

  const [products] = useState(() => buildSterileProcessingProducts());
  const [pricingBySlug, setPricingBySlug] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const bySlug = await fetchMicrocarePricing(API_URL);
        if (!cancelled) setPricingBySlug(bySlug);
      } catch (e) {
        console.error("MicroCare pricing fetch failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [API_URL]);

  const enrichedProducts = products.map((p) => {
    const enrich = pricingBySlug[p.id];
    if (!enrich || !enrich.default_variant) return p;
    const dv = enrich.default_variant;
    return {
      ...p,
      customer_price: dv.case_price ?? p.customer_price,
      unit_price: dv.unit_price ?? p.unit_price,
      case_price: dv.case_price ?? p.case_price,
      pack: dv.pack ?? p.pack,
      variants: enrich.variants,
    };
  });

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      technicalArticleSchema({
        headline: "Sterile Processing Command Center — MicroCare Detergents, Indicators & Compliance",
        description:
          "Sterile Mate's MicroCare-powered Sterile Processing command center: enzymatic detergents (ProEZ, ProClense), point-of-use treatment (ProEZ Foam, ProEZ Gel, QEZ), sterilization indicators (ProChek, ProView), disinfectants (ProSpray, V-Cide), and AAMI ST79/ST91/ST108-aligned compliance for hospital SPDs, ASCs, dental, and veterinary.",
        url: canonical,
        datePublished: "2026-02-24",
        dateModified: "2026-02-24",
        keywords: [
          "sterile processing command center",
          "MicroCare distributor Texas",
          "ProEZ enzymatic detergent",
          "ProChek sterilization indicators",
          "ProSpray hospital disinfectant",
          "AAMI ST79 SPD workflow",
          "AAMI ST91 endoscope reprocessing",
          "AAMI ST108 water for processing medical devices",
        ],
      }),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="sterile-processing-command-center-page">
      <SEO
        canonical={canonical}
        title="Sterile Processing Command Center — MicroCare Detergents, Indicators & Compliance (TX·LA·AL·MS)"
        description="MicroCare-powered Sterile Processing partner: ProEZ enzymatic detergents, point-of-use treatment, ProChek indicators, ProSpray disinfectants, and AAMI ST79/ST91/ST108-aligned compliance for hospital SPDs, ASCs, dental, and vet."
        keywords={[
          "sterile processing command center",
          "MicroCare distributor",
          "ProEZ enzymatic detergent",
          "ProChek sterilization indicators",
          "ProSpray hospital disinfectant",
          "AAMI ST79 SPD workflow",
          "AAMI ST91 endoscope reprocessing",
        ]}
        schema={schema}
      />
      <SiteHeader />

      {/* ── REBRAND BANNER ─────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-fuchsia-600 via-purple-600 to-indigo-600 text-white" data-testid="microcare-rebrand-banner">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-3 text-xs sm:text-sm text-center flex-wrap">
          <span className="inline-flex items-center gap-1.5 bg-white/15 rounded-full px-2.5 py-0.5 font-bold uppercase tracking-widest text-[10px]">
            <Sparkles className="w-3.5 h-3.5" /> Heads up
          </span>
          <span>
            <strong className="font-bold">Certol is now MicroCare Medical</strong>
            {" · "}
            <span className="opacity-90">same formula, new label.</span>
            {" "}
            <span className="opacity-75">You'll see both "old look" and "new look" packaging while inventory transitions.</span>
          </span>
        </div>
      </div>

      {/* ── HERO ────────────────────────────────────────────────────────── */}
      <section className="relative bg-gradient-to-br from-[#0a1a35] via-[#0b2447] to-[#2a1b6b] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(168,85,247,0.20),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(34,211,238,0.15),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-fuchsia-300 mb-4">
                Sterile Processing Command Center · Gulf Region
              </p>
              <h1 className="text-4xl md:text-6xl font-black leading-[1.03] max-w-3xl">
                One partner for every enzyme, every indicator, every SPD cycle.
              </h1>
              <p className="mt-6 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
                MicroCare enzymatic detergents, point-of-use treatments, sterilization indicators,
                surface disinfectants, and OSHA-compliant waste — dispatched from Houston to hospital
                SPDs, ASCs, dental & vet across{" "}
                <strong className="text-white">Texas, Louisiana, Alabama, and Mississippi.</strong>
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  asChild
                  size="lg"
                  className="bg-fuchsia-400 hover:bg-fuchsia-300 text-slate-900 font-bold shadow-lg shadow-fuchsia-500/25"
                  data-testid="sterile-cta-shop"
                >
                  <a href="#catalog">
                    <FlaskConical className="w-5 h-5 mr-2" />
                    Shop the Catalog
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                  data-testid="sterile-cta-audit"
                >
                  <a href="mailto:info@sterilemate.com?subject=SPD compliance audit">
                    <ShieldCheck className="w-5 h-5 mr-2" />
                    Request SPD Audit
                  </a>
                </Button>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
                <span className="inline-flex items-center gap-2"><Award className="w-4 h-4 text-fuchsia-300" /> certified small business Certified</span>
                <span className="inline-flex items-center gap-2"><Truck className="w-4 h-4 text-fuchsia-300" /> Same-day Gulf-region dispatch</span>
                <span className="inline-flex items-center gap-2"><Building2 className="w-4 h-4 text-fuchsia-300" /> Houston HQ · TMC-adjacent</span>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-fuchsia-900/40 ring-1 ring-white/10 bg-white/5 backdrop-blur">
                <div className="p-6 grid grid-cols-2 gap-3">
                  {enrichedProducts.slice(0, 4).map((p) => (
                    <div key={p.id} className="bg-white rounded-xl p-3 flex flex-col items-center">
                      <img
                        src={p.image}
                        alt={p.name}
                        className="w-full h-24 object-contain"
                        loading="eager"
                      />
                      <p className="mt-2 text-[11px] font-bold text-slate-900 text-center leading-tight line-clamp-2">
                        {p.name.replace(/™|®/g, "")}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="px-6 pb-5">
                  <p className="text-xs uppercase tracking-widest text-fuchsia-300 font-bold mb-1">
                    Full MicroCare line
                  </p>
                  <p className="text-white text-sm md:text-base font-semibold">
                    {products.length}+ SPD SKUs in stock — every enzyme, every indicator, every rinse.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STAT STRIP ─────────────────────────────────────────────────── */}
      <section className="bg-slate-900 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { n: `${products.length}+`, l: "SPD SKUs in-stock" },
            { n: "4", l: "States served" },
            { n: "24 hr", l: "Emergency dispatch" },
            { n: "ST79·ST91·ST108", l: "Standards mapped" },
          ].map((s) => (
            <div key={s.l} className="text-center">
              <p className="text-3xl md:text-4xl font-black text-white leading-none">{s.n}</p>
              <p className="mt-2 text-xs uppercase tracking-wider text-white/50">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FULL-STACK PILLARS ─────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-3xl mb-10">
          <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-fuchsia-700 mb-3">The Full Stack</p>
          <h2 className="text-3xl md:text-4xl font-black text-slate-900">Six pillars. One phone number.</h2>
          <p className="mt-4 text-slate-600 text-lg">
            Every SPD has to source enzymatic cleaners, sterilization indicators, lubricants, disinfectants,
            and waste packaging from someone. Most spend months coordinating five vendors. We consolidate the stack.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              className="group rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              data-testid={`sterile-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center mb-4 shadow-md shadow-fuchsia-500/20">
                <p.icon className="w-5 h-5 text-white" />
              </div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-fuchsia-700 mb-1">{p.kicker}</p>
              <p className="text-xl font-black text-slate-900 mb-2">{p.title}</p>
              <p className="text-sm text-slate-600 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CATALOG ─────────────────────────────────────────────────────── */}
      <section id="catalog" className="bg-slate-50 border-y border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-fuchsia-700 mb-3">Catalog</p>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              The MicroCare / Sterile Mate line — pick your SKU.
            </h2>
            <p className="mt-4 text-slate-600 text-lg">
              Every product below is stocked in Houston with live 2026 pricing merged from our
              authorized-distributor sheet. Add to cart to request an official quote with net-30 terms.
            </p>
          </div>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
            data-testid="sterile-catalog-grid"
          >
            {enrichedProducts.map((p) => {
              const price = money(p.case_price ?? p.customer_price);
              const unitPrice = money(p.unit_price);
              const openDetail = () => setSelectedProduct(p);
              return (
                <div
                  key={p.id}
                  className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-xl hover:-translate-y-0.5 hover:border-fuchsia-300 transition-all flex flex-col cursor-pointer"
                  onClick={openDetail}
                  data-testid={`sterile-product-${p.id}`}
                >
                  <div className="aspect-square bg-slate-100 flex items-center justify-center p-4 relative">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="max-w-full max-h-full object-contain group-hover:scale-[1.03] transition-transform"
                      loading="lazy"
                    />
                    {p.variants?.length > 1 && (
                      <span className="absolute top-2 right-2 text-[10px] font-bold uppercase bg-slate-900 text-white px-2 py-0.5 rounded shadow">
                        {p.variants.length} pack sizes
                      </span>
                    )}
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-fuchsia-700 mb-1">
                      MicroCare · SPD
                    </p>
                    <p className="text-sm font-black text-slate-900 leading-tight line-clamp-2 min-h-[2.5rem]">
                      {p.name}
                    </p>
                    <p className="mt-2 text-xs text-slate-600 leading-relaxed line-clamp-3">
                      {p.description}
                    </p>

                    {/* Key features preview — first 2 */}
                    {p.features?.length > 0 && (
                      <ul className="mt-3 space-y-1">
                        {p.features.slice(0, 2).map((f, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-[11px] text-slate-600 leading-snug">
                            <CheckCircle2 className="w-3 h-3 text-fuchsia-600 shrink-0 mt-0.5" />
                            <span className="line-clamp-1">{f}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Specs mini-table (SKU + Pack + Application) */}
                    <dl className="mt-3 grid grid-cols-2 gap-x-2 gap-y-1 text-[10px] leading-snug bg-slate-50 rounded-md p-2 border border-slate-100">
                      <dt className="text-slate-500 uppercase tracking-wide font-semibold">SKU</dt>
                      <dd className="text-slate-900 font-mono truncate">{p.specs?.SKU?.slice(0, 18) || p.id.slice(0, 18)}</dd>
                      <dt className="text-slate-500 uppercase tracking-wide font-semibold">Pack</dt>
                      <dd className="text-slate-900 truncate">{
                        (typeof p.pack === "string" && p.pack) ||
                        p.pack?.display ||
                        (typeof p.variants?.[0]?.pack === "string" ? p.variants[0].pack : p.variants?.[0]?.pack?.display) ||
                        "case"
                      }</dd>
                    </dl>

                    <div className="mt-3 flex-1" />

                    {/* Price */}
                    <div>
                      {price ? (
                        <div>
                          <p className="text-lg font-black text-slate-900">
                            {price}
                            <span className="text-xs font-medium text-slate-500 ml-1">/ case</span>
                          </p>
                          {unitPrice && (
                            <p className="text-[11px] text-slate-500">{unitPrice} per unit</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500 italic">Request pricing</p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetail();
                        }}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-2 h-9 rounded-lg border border-slate-300 hover:border-fuchsia-500 hover:text-fuchsia-700 text-slate-700 text-xs font-bold transition-colors"
                        data-testid={`sterile-view-details-${p.id}`}
                      >
                        View details
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          addItem({
                            erp: p.specs?.SKU || p.id.toUpperCase(),
                            name: p.name,
                            image: p.image,
                            source: "microcare",
                            category: "Sterile Processing",
                            unit_price: p.case_price ?? p.customer_price ?? 0,
                            list_price: p.case_price ?? p.customer_price ?? 0,
                            pack:
                              (typeof p.pack === "string" && p.pack) ||
                              p.pack?.display ||
                              "case",
                          });
                          setShowCart(true);
                        }}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-2 h-9 rounded-lg bg-slate-900 hover:bg-fuchsia-700 text-white text-xs font-bold transition-colors"
                        data-testid={`sterile-add-to-cart-${p.id}`}
                      >
                        <ShoppingCart className="w-3.5 h-3.5" />
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild variant="outline" data-testid="sterile-view-full-marketplace">
              <Link to="/marketplace?category=sterile-processing">
                See in full marketplace
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ── STANDARDS STACK ─────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-fuchsia-700 mb-3">Regulatory Stack</p>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              Every standard your surveyor references.
            </h2>
            <p className="mt-4 text-slate-600">
              Our SPD compliance binder maps every SOP, cycle log, and BI test result to the specific
              clause a Joint Commission, CMS, or state health-department surveyor will cite.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild variant="outline" data-testid="sterile-link-st91">
                <Link to="/resources/aami-st91">Read the ST91 guide</Link>
              </Button>
              <Button asChild variant="outline" data-testid="sterile-link-st108">
                <Link to="/resources/aami-st108">ST108 water guide</Link>
              </Button>
              <Button asChild variant="outline" data-testid="sterile-link-tir34">
                <Link to="/resources/aami-tir34">TIR34 technical report</Link>
              </Button>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-800">
                  <tr>
                    <th className="text-left px-5 py-3 font-bold w-56">Standard / Code</th>
                    <th className="text-left px-5 py-3 font-bold">What it covers</th>
                  </tr>
                </thead>
                <tbody>
                  {STANDARDS_STACK.map((s) => (
                    <tr key={s.code} className="border-t border-slate-100">
                      <td className="px-5 py-3 font-mono font-semibold text-slate-900">{s.code}</td>
                      <td className="px-5 py-3 text-slate-700">{s.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ── HEAD TO HEAD ────────────────────────────────────────────────── */}
      <section className="bg-slate-50 border-y border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-amber-700 mb-3">Head to Head</p>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              Why Gulf-region SPDs switch to us.
            </h2>
            <p className="mt-4 text-slate-600 text-lg">
              National SPD distributors optimize for scale. We optimize for the 6 AM phone call when
              your washer-disinfector detergent barrel just ran dry mid-cycle.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {COMPARE.map((c) => (
              <div key={c.them} className="rounded-2xl border border-slate-200 bg-white p-6">
                <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Compared to</p>
                <p className="text-lg font-black text-slate-900 mb-4">{c.them}</p>
                <div className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed">
                  <CheckCircle2 className="w-5 h-5 text-fuchsia-600 shrink-0 mt-0.5" />
                  <span>{c.us}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COVERAGE MAP ────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-8">
            <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-fuchsia-300 mb-3">Coverage</p>
            <h2 className="text-3xl md:text-4xl font-black">Gulf-region dispatch, one center.</h2>
            <p className="mt-4 text-white/70">
              Every route below is served weekly for scheduled restocks, with on-call emergency response.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {COVERAGE.map((c) => (
              <div key={c.state} className="rounded-2xl bg-white/5 border border-white/10 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-fuchsia-300" />
                  <p className="text-lg font-black text-white">{c.state}</p>
                </div>
                <p className="text-sm text-white/70 leading-relaxed">{c.locales}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-fuchsia-700 mb-3">Frequently Asked</p>
        <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-8">
          What buyers ask before they switch.
        </h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <details
              key={i}
              className="group rounded-xl border border-slate-200 bg-white p-5 open:shadow-md transition-shadow"
            >
              <summary className="cursor-pointer flex items-center justify-between font-bold text-slate-900 gap-4">
                {f.q}
                <ArrowRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform shrink-0" />
              </summary>
              <p className="mt-3 text-slate-700 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ── CLOSING CTA ────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-fuchsia-600 via-purple-700 to-indigo-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-black leading-tight">
              Talk to a real SPD specialist.
            </h2>
            <p className="mt-3 text-white/85 text-lg">
              One phone call · one email · one dispatch center. No 800-number call tree, no offshore support.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-white/90 font-bold" data-testid="sterile-cta-call">
              <a href="tel:2814563458">
                <Phone className="w-5 h-5 mr-2" /> 281.456.3458
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10" data-testid="sterile-cta-email">
              <a href="mailto:info@sterilemate.com?subject=SPD inquiry">
                <Mail className="w-5 h-5 mr-2" /> info@sterilemate.com
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10" data-testid="sterile-cta-catalog">
              <Link to="/marketplace?category=sterile-processing">
                <FileText className="w-5 h-5 mr-2" /> Full marketplace catalog
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Product detail modal — Overview, Features, Specs, Pack sizes, Applications */}
      <ProductDetailModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
    </div>
  );
}
